import React from 'react';
import { ReceptionContactInfo } from '../../models';
import { ChatStrings } from '../../i18n';
import { BellIcon, MailIcon, PhoneIcon } from '../Icons';

interface ReceptionCardProps {
   contact?: ReceptionContactInfo;
   strings: ChatStrings;
}

const EMAIL_PATTERN = /^[^\s@<>"'()]+@[^\s@<>"'()]+\.[^\s@<>"'()]+$/;

/** `tel:` target: digits plus an optional leading "+", nothing else. */
export function toTelHref(phone: string): string | undefined {
   const trimmed = phone.trim();
   const digits = trimmed.replace(/[^\d]/g, '');
   if (digits.length < 3) return undefined;
   return `tel:${trimmed.startsWith('+') ? '+' : ''}${digits}`;
}

export function toMailtoHref(email: string): string | undefined {
   const trimmed = email.trim();
   return EMAIL_PATTERN.test(trimmed) ? `mailto:${trimmed}` : undefined;
}

/**
 * The directory's standing "Reception" panel. Built ONLY from the server-provided `receptionContact`;
 * never invents a number or address.
 */
const ReceptionCard: React.FC<ReceptionCardProps> = ({ contact, strings }) => {
   const phones = (contact?.phones ?? []).map((p) => ({ label: p.trim(), href: toTelHref(p) })).filter((p) => p.href);
   const emails = (contact?.emails ?? []).map((e) => ({ label: e.trim(), href: toMailtoHref(e) })).filter((e) => e.href);
   const hasContact = phones.length > 0 || emails.length > 0;

   return (
      <section className="cx-reception" aria-label={strings.receptionTitle}>
         <h4 className="cx-reception__title">
            <BellIcon />
            <span>{strings.receptionTitle}</span>
         </h4>

         {hasContact ? (
            <ul className="cx-reception__list">
               {phones.map((p) => (
                  <li key={`tel-${p.href}`}>
                     <a className="cx-pill cx-frosted" href={p.href}>
                        <PhoneIcon />
                        <span>
                           <span className="cx-sr-only">{strings.call}: </span>
                           <span dir="ltr">{p.label}</span>
                        </span>
                     </a>
                  </li>
               ))}
               {emails.map((e) => (
                  <li key={`mail-${e.href}`}>
                     <a className="cx-pill cx-frosted" href={e.href}>
                        <MailIcon />
                        <span>
                           <span className="cx-sr-only">{strings.email}: </span>
                           <span dir="ltr">{e.label}</span>
                        </span>
                     </a>
                  </li>
               ))}
            </ul>
         ) : (
            <p className="cx-reception__body">{strings.receptionFallback}</p>
         )}
      </section>
   );
};

export default ReceptionCard;
